import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { CopyableCode, COPYABLE_CODE_CSS } from '../components/CopyableCode.jsx';
import {
  GlobeIcon,
  CheckIcon,
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  RefreshIcon,
  DeleteIcon,
  MailIcon,
} from '../components/icons';

/**
 * Email sender domains — tenant-scoped Resend integration.
 *
 * Two-step flow per RFC-036 sub-task 1:
 *   1. Root domains — claim the apex you own (e.g. `acmecorp.com`)
 *      by publishing a TXT record at `_spotlight-verify.{apex}`.
 *   2. Subdomains — once the root is verified, add any subdomain
 *      under it (`support.acmecorp.com`) for Resend to send from.
 *
 * The root-verification gate closes the spoof where a malicious
 * tenant could register `spotlight.help` or someone else's domain
 * as their sending subdomain.
 */
export function EmailDomains() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [domains, setDomains] = useState([]);
  const [resendConfigured, setResendConfigured] = useState(true);
  const [rootDomains, setRootDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmNode, ask] = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [resp, roots] = await Promise.all([
        api('/v1/admin/email/domains'),
        api('/v1/admin/email/root-domains').catch(() => ({ root_domains: [] })),
      ]);
      setDomains(resp?.domains || []);
      setResendConfigured(Boolean(resp?.resend_configured));
      setRootDomains(roots?.root_domains || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const verifiedApices = useMemo(
    () => rootDomains.filter((r) => r.verified).map((r) => r.apex),
    [rootDomains],
  );

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <style>{COPYABLE_CODE_CSS}</style>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Email sender domains</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          The "from" address Pinpoint uses when an operator replies
          to a help thread or sends a follow-up. Without a sender
          domain here, replies go out as
          <code> no-reply@spotlight.help</code> — fine for getting
          started, terrible once a real user hits reply and you'd
          rather they reached your inbox, not ours.
        </p>
      </div>

      <section className="card" style={{ background: 'var(--surface-1)', padding: 14 }}>
        <h3 className="card__title" style={{ marginBottom: 6 }}>How it works</h3>
        <ol style={{ margin: '4px 0 10px 18px', color: 'var(--text-2)', fontSize: 13.5, lineHeight: 1.55 }}>
          <li>
            <strong>Claim the root domain</strong> you control
            (e.g. <code>acmecorp.com</code>). We mint a verification
            token; publish it as a TXT record at
            <code> _spotlight-verify.&lt;your-domain&gt;</code>.
            Without this step we can't trust that you actually own
            the apex.
          </li>
          <li>
            <strong>Add a sending subdomain</strong> under that
            apex (<code>support.acmecorp.com</code>). Once the apex
            is verified, the subdomain form unlocks. We register it
            with the email-delivery provider and surface the SPF /
            DKIM / DMARC records for you to publish.
          </li>
        </ol>
        <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 10, marginBottom: 0 }}>
          Platform notifications like teammate invites and billing
          receipts always come from <code>noreply@spotlight.help</code>
          regardless of what's configured here — they identify
          Spotlight, not your brand.
        </p>
      </section>

      <RootDomainsSection
        api={api}
        rootDomains={rootDomains}
        onChanged={load}
      />

      {!resendConfigured && (
        <div className="state">
          <div className="state__title">Email delivery isn't configured yet</div>
          <div>The email-sending credentials aren't set on this deployment. Ask whoever runs the install to finish the tenant email setup before adding domains.</div>
        </div>
      )}

      {resendConfigured && (
        <AddDomainForm
          api={api}
          onCreated={load}
          verifiedApices={verifiedApices}
        />
      )}

      {loading && <div className="state"><div className="state__title">Loading domains…</div></div>}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load domains</div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && resendConfigured && domains.length === 0 && (
        <div className="state">
          <div className="state__title">No subdomains yet</div>
          <div>
            {verifiedApices.length === 0
              ? 'Claim and verify a root domain first, then add a sending subdomain.'
              : 'Add the first one above. You\'ll need DNS-edit access to publish the verification records.'}
          </div>
        </div>
      )}

      {!loading && !error && domains.length > 0 && (
        <ul className="list">
          {domains.map((d) => (
            <DomainRow key={d.domain_id} d={d} api={api} onChanged={load} ask={ask} />
          ))}
        </ul>
      )}

      {confirmNode}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Root domains — RFC-036 sub-task 1
// ---------------------------------------------------------------------------

function RootDomainsSection({ api, rootDomains, onChanged }) {
  return (
    <section className="card">
      <h3 className="card__title">Root domains</h3>
      <p style={{ color: 'var(--text-3)', fontSize: 13.5, margin: '4px 0 12px' }}>
        Prove you control the apex domain before adding sending
        subdomains under it.
      </p>
      <AddRootDomainForm api={api} onCreated={onChanged} />
      {rootDomains.length > 0 && (
        <ul className="list" style={{ marginTop: 14 }}>
          {rootDomains.map((r) => (
            <RootDomainRow
              key={r.apex}
              row={r}
              api={api}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function AddRootDomainForm({ api, onCreated }) {
  const [apex, setApex] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api('/v1/admin/email/root-domains', {
        method: 'POST',
        body: JSON.stringify({ apex: apex.trim() }),
      });
      setApex('');
      onCreated();
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} style={{
      display: 'grid', gridTemplateColumns: '3fr auto', gap: 12, alignItems: 'end',
    }}>
      <label className="field">
        <span className="field__label">Apex domain you own</span>
        <input
          className="input"
          value={apex}
          onChange={(e) => setApex(e.target.value)}
          placeholder="acmecorp.com"
          required
        />
      </label>
      <button type="submit" className="btn btn--primary" disabled={busy || !apex.trim()}>
        <GlobeIcon /> {busy ? 'Claiming…' : 'Claim domain'}
      </button>
      {err && <p style={{ color: 'var(--danger-700)', gridColumn: '1 / -1', margin: 0 }}>{err}</p>}
    </form>
  );
}

function RootDomainRow({ row, api, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function verify() {
    setBusy(true);
    setErr(null);
    try {
      await api(
        `/v1/admin/email/root-domains/${encodeURIComponent(row.apex)}/verify`,
        { method: 'POST' },
      );
      onChanged();
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  }

  return (
    <li className="list__row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        <div style={{ flex: 1 }}>
          <span className="list__name">
            {row.apex}
            <span
              className={`badge ${row.verified ? 'badge--editor' : 'badge--default'}`}
              style={{ marginLeft: 8 }}
            >
              {row.verified ? 'verified' : 'pending'}
            </span>
          </span>
          {row.verified_at && (
            <span className="list__meta">
              verified {new Date(row.verified_at).toLocaleString()}
            </span>
          )}
        </div>
        {!row.verified && (
          <button type="button" className="btn" onClick={verify} disabled={busy}>
            <CheckIcon /> {busy ? 'Checking DNS…' : 'Verify'}
          </button>
        )}
      </div>
      {!row.verified && (
        <div style={{
          marginTop: 10, background: 'var(--surface-1)', padding: 12, borderRadius: 8,
        }}>
          <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--text-2)' }}>
            Publish a TXT record at:
          </p>
          <CopyableCode value={row.verification_host} label="record name" />
          <p style={{ margin: '8px 0 6px', fontSize: 13, color: 'var(--text-2)' }}>
            With value:
          </p>
          <CopyableCode value={row.verification_token} label="record value" />
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
            DNS propagation can take a few minutes. Once published,
            click Verify above.
          </p>
        </div>
      )}
      {err && <p style={{ color: 'var(--danger-700)', marginTop: 8, marginBottom: 0 }}>{err}</p>}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Subdomain add — now gated on a verified apex (UI-side: hidden until then)
// ---------------------------------------------------------------------------

function AddDomainForm({ api, onCreated, verifiedApices }) {
  const [name, setName] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const enabled = verifiedApices.length > 0;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api('/v1/admin/email/domains', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), region }),
      });
      setName('');
      onCreated();
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  }

  return (
    <section className="card">
      <h3 className="card__title">Add a sending subdomain</h3>
      {!enabled && (
        <p style={{ color: 'var(--text-3)', fontSize: 13.5, margin: '6px 0 0' }}>
          Verify a root domain above before adding a subdomain under it.
        </p>
      )}
      {enabled && (
        <p style={{ color: 'var(--text-3)', fontSize: 13.5, margin: '6px 0 0' }}>
          Pick a subdomain of one of your verified apices:
          {' '}
          {verifiedApices.map((a) => <code key={a} style={{ marginRight: 6 }}>{a}</code>)}
        </p>
      )}
      <form onSubmit={submit} style={{
        display: 'grid', gridTemplateColumns: '3fr 1fr auto',
        gap: 12, alignItems: 'end', marginTop: 10,
      }}>
        <label className="field">
          <span className="field__label">Subdomain (e.g. support.{verifiedApices[0] || 'your-domain.com'})</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={verifiedApices[0] ? `support.${verifiedApices[0]}` : 'support.acmecorp.com'}
            required
            disabled={!enabled}
          />
        </label>
        <label className="field">
          <span className="field__label">Region</span>
          <select
            className="select"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={!enabled}
          >
            <option value="us-east-1">us-east-1</option>
            <option value="eu-west-1">eu-west-1</option>
            <option value="sa-east-1">sa-east-1</option>
          </select>
        </label>
        <button type="submit" className="btn btn--primary" disabled={busy || !enabled}>
          <AddIcon /> {busy ? 'Adding…' : 'Add subdomain'}
        </button>
      </form>
      {err && <p style={{ color: 'var(--danger-700)', marginTop: 12 }}>{err}</p>}
    </section>
  );
}

function DomainRow({ d, api, onChanged, ask }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const status = (d.status || 'not_started').toLowerCase();
  const verified = status === 'verified';
  const inboundEnabled = Boolean(d.inbound_enabled);
  const inboundVerified = Boolean(d.inbound_verified_at);

  async function verify() {
    setBusy(true);
    try {
      await api(`/v1/admin/email/domains/${encodeURIComponent(d.domain_id)}/verify`, { method: 'POST' });
      onChanged();
    } finally { setBusy(false); }
  }

  function remove() {
    ask({
      title: `Delete domain "${d.name}"?`,
      body: 'Future sends from this domain will fall back to the platform default.',
      confirmLabel: 'Delete',
      danger: true,
      runOnConfirm: async () => {
        await api(`/v1/admin/email/domains/${encodeURIComponent(d.domain_id)}`, { method: 'DELETE' });
        onChanged();
      },
    });
  }

  return (
    <li className="list__row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <span className="list__name">
            {d.name}
            <span className={`badge ${verified ? 'badge--editor' : 'badge--default'}`} style={{ marginLeft: 8 }}>
              {status}
            </span>
            {inboundEnabled && (
              <span
                className={`badge ${inboundVerified ? 'badge--editor' : 'badge--default'}`}
                style={{ marginLeft: 6 }}
                title={inboundVerified
                  ? `Inbound verified ${new Date(d.inbound_verified_at).toLocaleString()}`
                  : 'Inbound enabled — publish the MX records and click Verify inbound'}
              >
                inbound {inboundVerified ? 'verified' : 'pending'}
              </span>
            )}
          </span>
          <span className="list__meta">{d.region || '—'}</span>
        </div>
        <button type="button" className="btn" onClick={() => setOpen((o) => !o)}>
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
          {open ? 'Hide records' : 'Show DNS records'}
        </button>
        <button type="button" className="btn" onClick={verify} disabled={busy}>
          <RefreshIcon /> {busy ? '…' : 'Verify'}
        </button>
        <button type="button" className="btn btn--danger" onClick={remove} disabled={busy}>
          <DeleteIcon /> Delete
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 12, background: 'var(--surface-1)', borderRadius: 8, padding: 12 }}>
          {(!d.records || d.records.length === 0) ? (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-3)' }}>
              We don't have the DNS records yet — try again in a moment. Click Verify in a few seconds.
            </p>
          ) : (
            <table className="resource-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Value</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.records.map((r, i) => (
                  <tr key={i}>
                    <td><code>{r.type || r.record || '—'}</code></td>
                    <td><code style={{ wordBreak: 'break-all' }}>{r.name || '—'}</code></td>
                    <td><code style={{ wordBreak: 'break-all', fontSize: 11 }}>{r.value || '—'}</code></td>
                    <td>{r.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {/* Inbound panel — only meaningful once the subdomain itself is
          verified for outbound; before that, the operator can't enable
          inbound at all (the backend rejects the call) so we hide the
          surface entirely to keep the row uncluttered. */}
      {verified && (
        <InboundPanel d={d} api={api} onChanged={onChanged} />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Inbound parse — RFC-036 sub-task 2
// ---------------------------------------------------------------------------

/**
 * Per-domain inbound enablement surface.
 *
 *   - When inbound is OFF: an "Enable inbound" CTA + the brand-benefit
 *     copy ("replies come back to YOUR domain, not ours") so the
 *     operator understands what they're opting into.
 *   - When inbound is ON but unverified: the MX records to publish,
 *     each with a Copy button (mirror the root-domain TXT pattern),
 *     and a Verify inbound button that polls until the records
 *     resolve.
 *   - When inbound is VERIFIED: a quiet "Replies now route to
 *     replies@{subdomain}" confirmation.
 */
function InboundPanel({ d, api, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const enabled = Boolean(d.inbound_enabled);
  const verified = Boolean(d.inbound_verified_at);
  const mxRecords = d.inbound_mx_records || [];

  async function enable() {
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      const resp = await api(
        `/v1/admin/email/domains/${encodeURIComponent(d.domain_id)}/enable-inbound`,
        { method: 'POST' },
      );
      // ``remote_ack=false`` means Resend's API didn't accept the
      // toggle (their inbound API isn't on every account yet) but
      // the MX records are persisted — operator can still publish.
      if (resp && resp.remote_ack === false) {
        setNotice(
          'MX records published. The email-delivery provider didn\'t '
          + 'accept the auto-toggle for inbound parsing — an operator '
          + 'may need to enable inbound for this domain once in the '
          + 'provider\'s console. The DNS side is ready either way.',
        );
      }
      onChanged();
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  }

  async function verifyInbound() {
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      await api(
        `/v1/admin/email/domains/${encodeURIComponent(d.domain_id)}/verify-inbound`,
        { method: 'POST' },
      );
      onChanged();
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  }

  return (
    <div style={{
      marginTop: 12, background: 'var(--surface-1)', borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13.5 }}>Inbound parsing</strong>
        {!enabled && (
          <button type="button" className="btn btn--primary" onClick={enable} disabled={busy}>
            <MailIcon /> {busy ? 'Enabling…' : 'Enable inbound'}
          </button>
        )}
        {enabled && !verified && (
          <button type="button" className="btn" onClick={verifyInbound} disabled={busy}>
            <CheckIcon /> {busy ? 'Checking DNS…' : 'Verify inbound'}
          </button>
        )}
      </div>
      {!enabled && (
        <p style={{ color: 'var(--text-3)', fontSize: 13, margin: '8px 0 0' }}>
          By default, replies to your help threads route back to
          <code> @spotlight.help</code> — looks like a relay and some
          recipients suspicion-block. Enable inbound on this subdomain
          and Pinpoint will set <code>Reply-To: spotlight-help+&lt;id&gt;@{d.name}</code>,
          so replies come back to <strong>your</strong> domain. You'll
          need DNS-edit access to publish one MX record.
        </p>
      )}
      {enabled && !verified && mxRecords.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--text-2)' }}>
            Publish this MX record on <code>{d.name}</code>:
          </p>
          {mxRecords.map((r, i) => (
            <MxRecordRow key={i} record={r} />
          ))}
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
            DNS propagation can take a few minutes. Once published,
            click Verify inbound.
          </p>
        </div>
      )}
      {verified && (
        <p style={{ color: 'var(--text-3)', fontSize: 13, margin: '6px 0 0' }}>
          Replies on this tenant now route to
          <code> spotlight-help+&lt;request_id&gt;@{d.name}</code>.
          Verified {new Date(d.inbound_verified_at).toLocaleString()}.
        </p>
      )}
      {notice && (
        <p style={{ color: 'var(--text-2)', fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>
          {notice}
        </p>
      )}
      {err && (
        <p style={{ color: 'var(--danger-700)', marginTop: 8, marginBottom: 0, fontSize: 13 }}>
          {err}
        </p>
      )}
    </div>
  );
}

function MxRecordRow({ record }) {
  const cell = (label, field, value) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)', minWidth: 56 }}>{label}</span>
      <CopyableCode value={value} label={label.toLowerCase()} />
    </div>
  );

  return (
    <div style={{
      display: 'grid', gap: 4, padding: 8, background: 'var(--surface-2)',
      borderRadius: 6, marginTop: 4,
    }}>
      {cell('Type', 'type', record.type || 'MX')}
      {cell('Name', 'name', record.name)}
      {cell('Priority', 'priority', record.priority)}
      {cell('Value', 'value', record.value)}
    </div>
  );
}
